import { useState, useEffect, useCallback } from 'react';
import { collection, query, getDocs, updateDoc, doc, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { immunizationSchedule } from '../helpers/immunizationSchedule';
import { calculateAgeMonths } from '../helpers/utils';

const READ_NOTIFICATIONS_KEY = 'readNotifications';

export const useNotifications = (user, myChildren, loadingChildren) => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [dbNotifications, setDbNotifications] = useState([]);
    const [readNotificationIds, setReadNotificationIds] = useState(() => {
        try {
            const storedIds = localStorage.getItem(READ_NOTIFICATIONS_KEY);
            return storedIds ? new Set(JSON.parse(storedIds)) : new Set();
        } catch (error) {
            console.error("Error reading read notifications from localStorage:", error);
            return new Set();
        }
    });

    // Fetch real notifications from Firestore (Alerts, Warnings)
    const fetchDbNotifications = useCallback(async () => {
        if (!user || user.isAnonymous) return;
        try {
            const q = query(collection(db, `users/${user.uid}/notifications`), orderBy('date', 'desc'));
            const querySnapshot = await getDocs(q);
            const alerts = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                isDb: true // Flag to identify Firestore notifications
            }));
            setDbNotifications(alerts);
        } catch (error) {
            console.error("Error fetching notifications from DB:", error);
        }
    }, [user]);

    useEffect(() => {
        fetchDbNotifications();
    }, [fetchDbNotifications]);

    // Local Mock logic for immunization reminders
    const generateLocalImmunizationNotifications = useCallback(() => {
        if (loadingChildren || !myChildren || myChildren.length === 0) return [];

        let generated = [];
        for (const child of myChildren) {
            const ageMonths = calculateAgeMonths(child.dob.toDate());
            const immunizationsData = {}; 

            for (const schedule of immunizationSchedule) {
                if (ageMonths >= schedule.age && ageMonths < schedule.age + 2 && !immunizationsData[schedule.name]?.done) {
                    generated.push({
                        id: `imunisasi-${child.id}-${schedule.name}`,
                        title: `Jadwal Imunisasi Mendatang`,
                        description: `Ananda ${child.name} dijadwalkan untuk imunisasi ${schedule.name} (usia ${schedule.age} bulan).`,
                        date: `Pengingat Otomatis`,
                        icon: '<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>',
                        type: 'immunization',
                        childId: child.id,
                        urgency: 'low',
                        isDb: false
                    });
                }
            }
        }
        return generated;
    }, [myChildren, loadingChildren]);

    useEffect(() => {
        const localNotifications = generateLocalImmunizationNotifications();
        
        // Merge DB Notifications and Local Notifications
        // For DB notifications, use the 'read' property from Firestore. 
        // For Local, use localStorage 'readNotificationIds'.
        
        const formattedDbNotifications = dbNotifications.map(notif => ({
            ...notif,
            date: notif.date?.toDate ? notif.date.toDate().toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : notif.date,
        }));

        const formattedLocalNotifications = localNotifications.map(notif => ({
            ...notif,
            read: readNotificationIds.has(notif.id),
        }));

        const combined = [...formattedDbNotifications, ...formattedLocalNotifications];
        
        setNotifications(combined);
        setUnreadCount(combined.filter(notif => !notif.read).length);
    }, [generateLocalImmunizationNotifications, dbNotifications, readNotificationIds]);

    const markAsRead = async (id, isDb) => {
        if (isDb) {
            // Update read status in Firestore
            try {
                const notifRef = doc(db, `users/${user.uid}/notifications/${id}`);
                await updateDoc(notifRef, { read: true });
                // Re-fetch to update state
                fetchDbNotifications();
            } catch (error) {
                console.error("Failed to mark notification as read in DB", error);
            }
        } else {
            // Update local storage for mock notifications
            setReadNotificationIds(prev => {
                const newSet = new Set(prev);
                newSet.add(id);
                localStorage.setItem(READ_NOTIFICATIONS_KEY, JSON.stringify(Array.from(newSet)));
                return newSet;
            });
        }
    };

    return { notifications, unreadCount, markAsRead };
};
